import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ChevronLeft, ChevronRight, CheckCircle2,
  BookOpen, HelpCircle, Award, Loader2, Play
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import api from "../../lib/api";

const InteractiveCoursePlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState({});
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cRes, sRes] = await Promise.all([
          api.get(`/courses/${courseId}`),
          api.get(`/courses/${courseId}/content`)
        ]);
        setCourse(cRes.data);
        setSlides(sRes.data?.slides || []);
      } catch (e) { toast.error("Failed to load course"); console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [courseId]);

  const updateProgress = async (slideIdx) => {
    const progress = Math.round(((slideIdx + 1) / slides.length) * 100);
    try {
      await api.post(`/courses/${courseId}/progress`, { progress });
      if (progress >= 100) setCompleted(true);
    } catch (e) { /* ignore if not enrolled */ }
  };

  const goTo = (idx) => {
    if (idx >= 0 && idx < slides.length) {
      setCurrent(idx);
      updateProgress(idx);
    }
  };

  const handleQuizAnswer = (slideId, qIdx, optIdx) => {
    if (quizSubmitted[slideId]) return;
    setQuizAnswers(prev => ({ ...prev, [`${slideId}-${qIdx}`]: optIdx }));
  };

  const submitQuiz = (slideId, quiz) => {
    let correct = 0;
    quiz.forEach((q, i) => {
      if (quizAnswers[`${slideId}-${i}`] === q.answer) correct++;
    });
    setQuizSubmitted(prev => ({ ...prev, [slideId]: { correct, total: quiz.length } }));
    if (correct === quiz.length) toast.success("Perfect score!");
    else toast.info(`${correct}/${quiz.length} correct`);
  };

  const progress = slides.length > 0 ? Math.round(((current + 1) / slides.length) * 100) : 0;

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!slides.length) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <BookOpen className="w-12 h-12 mx-auto text-muted-foreground" />
        <p className="text-muted-foreground">No content available for this course</p>
        <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    </div>
  );

  const slide = slides[current];
  const typeColors = { intro: "from-blue-600 to-indigo-700", content: "from-slate-700 to-slate-900", quiz: "from-purple-600 to-violet-700", summary: "from-green-600 to-emerald-700" };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b px-6 py-3">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Exit
            </Button>
            <span className="text-sm font-medium truncate max-w-[300px]">{course?.title}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{current + 1}/{slides.length}</span>
            <Progress value={progress} className="w-32 h-2" />
            <span className="text-xs font-medium">{progress}%</span>
          </div>
        </div>
      </div>

      {/* Slide Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-3xl"
          >
            {/* Intro / Summary Slide */}
            {(slide.type === "intro" || slide.type === "summary") && (
              <Card className="overflow-hidden">
                <div className={`bg-gradient-to-br ${typeColors[slide.type]} p-8 text-white text-center`}>
                  <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
                    {slide.type === "intro" ? <Play className="w-8 h-8" /> : <Award className="w-8 h-8" />}
                  </div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="slide-title">{slide.title}</h2>
                </div>
                <CardContent className="p-8">
                  <div className="prose prose-sm max-w-none">
                    {slide.content.split("\n").map((line, i) => {
                      if (line.startsWith("**") && line.endsWith("**")) return <h4 key={i} className="font-bold text-base mt-3 mb-1">{line.replace(/\*\*/g, "")}</h4>;
                      if (line.startsWith("- ")) return <li key={i} className="ml-4 text-sm text-muted-foreground">{renderBold(line.slice(2))}</li>;
                      if (line.trim() === "") return <br key={i} />;
                      return <p key={i} className="text-sm text-muted-foreground leading-relaxed">{renderBold(line)}</p>;
                    })}
                  </div>
                  {completed && slide.type === "summary" && (
                    <div className="mt-6 p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-center">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="font-semibold text-green-700">Course Completed!</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Content Slide */}
            {slide.type === "content" && (
              <Card>
                <CardContent className="p-8">
                  <Badge variant="outline" className="mb-4">{slide.type}</Badge>
                  <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="slide-title">{slide.title}</h2>
                  <div className="prose prose-sm max-w-none">
                    {slide.content.split("\n").map((line, i) => {
                      if (line.startsWith("**") && line.endsWith("**")) return <h4 key={i} className="font-bold text-base mt-4 mb-2">{line.replace(/\*\*/g, "")}</h4>;
                      if (line.startsWith("- ")) return <li key={i} className="ml-4 text-sm leading-relaxed">{renderBold(line.slice(2))}</li>;
                      if (line.trim() === "") return <br key={i} />;
                      return <p key={i} className="text-sm leading-relaxed">{renderBold(line)}</p>;
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quiz Slide */}
            {slide.type === "quiz" && (
              <Card>
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <HelpCircle className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="slide-title">{slide.title}</h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">{slide.content}</p>
                  <div className="space-y-6">
                    {slide.quiz?.map((q, qi) => {
                      const selected = quizAnswers[`${slide.id}-${qi}`];
                      const submitted = quizSubmitted[slide.id];
                      const isCorrect = submitted && selected === q.answer;
                      return (
                        <div key={qi} className="space-y-2" data-testid={`quiz-q-${qi}`}>
                          <p className="font-medium text-sm">{qi + 1}. {q.q}</p>
                          <div className="grid gap-2">
                            {q.options.map((opt, oi) => (
                              <button
                                key={oi}
                                onClick={() => handleQuizAnswer(slide.id, qi, oi)}
                                disabled={!!submitted}
                                className={`text-left p-3 rounded-lg border-2 text-sm transition-all ${
                                  submitted
                                    ? oi === q.answer
                                      ? "border-green-500 bg-green-500/5"
                                      : selected === oi
                                      ? "border-red-500 bg-red-500/5"
                                      : "border-border opacity-50"
                                    : selected === oi
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-primary/30"
                                }`}
                                data-testid={`quiz-opt-${qi}-${oi}`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                                    submitted
                                      ? oi === q.answer
                                        ? "border-green-500 bg-green-500 text-white"
                                        : selected === oi
                                        ? "border-red-500 bg-red-500 text-white"
                                        : "border-muted"
                                      : selected === oi
                                      ? "border-primary bg-primary text-primary-foreground"
                                      : "border-muted"
                                  }`}>
                                    {String.fromCharCode(65 + oi)}
                                  </div>
                                  {opt}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {!quizSubmitted[slide.id] && (
                      <Button
                        onClick={() => submitQuiz(slide.id, slide.quiz)}
                        disabled={!slide.quiz?.every((_, i) => quizAnswers[`${slide.id}-${i}`] !== undefined)}
                        data-testid="submit-quiz-btn"
                      >
                        Check Answers
                      </Button>
                    )}
                    {quizSubmitted[slide.id] && (
                      <div className={`p-3 rounded-lg text-sm font-medium ${quizSubmitted[slide.id].correct === quizSubmitted[slide.id].total ? "bg-green-500/10 text-green-700" : "bg-amber-500/10 text-amber-700"}`}>
                        {quizSubmitted[slide.id].correct}/{quizSubmitted[slide.id].total} correct
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t px-6 py-4">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          <Button
            variant="outline"
            onClick={() => goTo(current - 1)}
            disabled={current === 0}
            data-testid="prev-slide-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>

          {/* Slide dots */}
          <div className="flex gap-1.5">
            {slides.map((s, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  i === current ? "bg-primary w-6" : i <= current ? "bg-primary/50" : "bg-muted"
                }`}
                data-testid={`slide-dot-${i}`}
              />
            ))}
          </div>

          {current < slides.length - 1 ? (
            <Button onClick={() => goTo(current + 1)} data-testid="next-slide-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => { updateProgress(slides.length - 1); navigate(-1); toast.success("Course completed!"); }}
              data-testid="finish-course-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Finish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper to render bold text in markdown-like format
function renderBold(text) {
  const parts = text.split(/(\*\*.*?\*\*)/);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default InteractiveCoursePlayer;
